import {
  milliseconds as getMilliseconds,
  millisecondsToHours,
  millisecondsToMinutes,
} from 'date-fns';

export const TIMEZONE = 'Australia/Sydney';

/**
 * Duration in hours between two supplied time.
 * Not using date-fns since date-fns provides "distance" rather than number as in hours.
 * @param startTime first time used to calculate the duration.
 * @param endTime second time used to calculate the duration.
 * @returns duration in hours between two times.
 */
export const durationInHours = (
  startTime?: Date | number,
  endTime?: Date | number
): number => {
  if (startTime === undefined || endTime === undefined) {
    return 0;
  }

  return parseFloat(
    // It is impossible to get 0 division here because the denominator is constant
    // eslint-disable-next-line total-functions/no-partial-division
    ((endTime.valueOf() - startTime.valueOf()) / (3600 * 1000)).toFixed(1)
  );
};

export const shiftTimeFormat = 'HH:mm';

export const shiftDateDataIndexFormat = 'yyyyMMdd';

export const shiftDateFormat = 'dd MMM yyyy';

export const rosterLongHeaderDateFormat = 'E dd MMM';

export const shiftDateTimeFormat = "PPP 'at' HH:mm";

export const tableDateTimeFormat = 'yyyy-MM-dd HH:mm';

export const reportDateTimeFormat = 'yyyy-MM-dd HH:mm:ss';

/**
 * Use custom function because date-fns floors the hour value
 * @param ms time value in ms
 * @returns number of hours
 */
export const customMillisecondsToHours = (ms: number) =>
  // Also impossible to get 0 division here
  // eslint-disable-next-line total-functions/no-partial-division
  Math.round(ms / (1000 * 60 * 60));

/**
 * Returns a number formatted into a string that is two digits long, such as is used in military time format.
 * Note: this assumes that the given number is <= 99. Numbers > 99 will currently lose the first digit.
 */
const twoDigitsTime = (time: number) => `0${time}`.slice(-2);

/**
 * Outputs a string indicating the 24hrs military-formatted time
 * Note: If the given milliseconds is >= 100 hrs, only the two right-most digits will be displayed (see docs for `twoDigitsTime`)
 * @param milliseconds an amount of time in milliseconds
 * @returns military time based on the input number. e.g. 25200000 -> "0700"
 */
export const militaryTimeString = (milliseconds: number) => {
  const hours = millisecondsToHours(milliseconds); //Floors the hour value
  const minutes = millisecondsToMinutes(
    milliseconds -
      getMilliseconds({
        hours: hours,
      })
  );

  return `${twoDigitsTime(hours)}${twoDigitsTime(minutes)}`;
};

export const asDateNumber = (date: string | Date | number): number =>
  new Date(date).valueOf();
